import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
// A brand is the existing product catalogue's text value, not a new entity.
export const brandInfo = z.object({
    name: z.string(),
    productCount: z.number().int().nonnegative(),
    activeProductCount: z.number().int().nonnegative(),
});
export const listBrandsQuery = paginationQuerySchema.extend({
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
    search: z.string().max(255).optional(),
});
export const brandsListResponse = pagedResponse(brandInfo).extend({
    totalBrands: z.number().int().nonnegative(),
    totalProducts: z.number().int().nonnegative(),
    productsWithoutBrand: z.number().int().nonnegative(),
});
const date = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((v) => {
    const time = Date.parse(`${v}T00:00:00Z`);
    return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === v;
}, "Invalid date");
const sampleFields = { storeId: z.string().uuid().optional(), from: date, to: date };
const validRange = (v) => v.from <= v.to && Date.parse(v.to) - Date.parse(v.from) <= 92 * 86400000;
const rangeMessage = "Choose an ordered date range of at most 93 days";
export const brandSampleQuery = z.object(sampleFields).refine(validRange, rangeMessage);
export const catalogEvidenceQuery = paginationQuerySchema
    .extend({
    ...sampleFields,
    page: z.coerce.number().int().min(1).max(1000000).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(6),
    brand: z.string().min(1).optional(),
    categoryId: z.string().uuid().optional(),
})
    .refine(validRange, rangeMessage);
const count = z.number().int().nonnegative();
const metric = z.object({
    numerator: count,
    denominator: count,
    value: z.number().min(0).max(100).nullable(),
});
const evidence = z.object({ captureId: z.string().uuid(), capturedAt: z.string().datetime() });
export const catalogEvidenceResponse = pagedResponse(evidence);
const groupObservation = z.object({
    name: z.string(),
    facings: count,
    productCount: count,
    imageCount: count,
    frontFacings: count,
    backFacings: count,
    sideFacings: count,
    unknownOrientationFacings: count,
    needsReviewFacings: count,
    observedShare: metric,
    attributedShare: metric,
    evidence: z.array(evidence),
});
const missingLink = z.object({
    reason: z.enum(["missing_product", "missing_brand", "missing_category"]),
    facings: count,
    detections: count,
});
export const brandSampleResponse = z.object({
    definitionVersion: z.literal("catalog-image-sample/v1"),
    brandSource: z.literal("products.brand"),
    categorySource: z.literal("products.category_id"),
    from: z.string(),
    to: z.string(),
    computedAt: z.string().datetime(),
    observedThrough: z.string().datetime().nullable(),
    captures: count,
    analyzedCaptures: count,
    productDetections: count,
    observedFacings: count,
    attribution: metric,
    categoryAttribution: metric,
    needsReviewDetections: count,
    unattributed: z.array(missingLink),
    uncategorized: z.array(missingLink),
    brands: z.array(groupObservation.extend({ categoryCount: count })),
    categories: z.array(groupObservation.extend({ id: z.string().uuid(), brandCount: count })),
    evidence: z.array(evidence),
});
export const catalogTrendQuery = z
    .object({
    ...sampleFields,
    dimension: z.enum(["brand", "category"]),
    // Omitted means the combined observation trend for all linked groups.
    key: z.string().min(1).optional(),
    bucket: z.enum(["day", "week"]).default("day"),
})
    .refine(validRange, rangeMessage)
    .refine((v) => v.key === undefined ||
    v.dimension !== "category" ||
    z.string().uuid().safeParse(v.key).success, "A focused category trend requires a category public ID");
export const catalogTrendResponse = z.object({
    definitionVersion: z.literal("catalog-image-trend/v1"),
    from: date,
    to: date,
    dimension: z.enum(["brand", "category"]),
    key: z.string().nullable(),
    bucket: z.enum(["day", "week"]),
    computedAt: z.string().datetime(),
    points: z
        .array(z.object({
        date,
        through: date,
        captures: count,
        analyzedCaptures: count,
        observedFacings: count,
        linkedFacings: count,
        facings: count,
        productCount: count,
        imageCount: count,
        frontFacings: count,
        observedShare: metric,
        frontFacingShare: metric,
        attribution: metric,
    }))
        .max(93),
});
export const brandReportQuery = z
    .object({
    ...sampleFields,
    brand: z.string().trim().min(1).max(255),
})
    .refine(validRange, rangeMessage);
export const categoryReportQuery = z
    .object({
    ...sampleFields,
    categoryId: z.string().uuid(),
})
    .refine(validRange, rangeMessage);
const reportQuality = z.object({
    status: z.enum(["unavailable", "limited", "uneven", "comparable_scope"]),
    reasons: z.array(z.object({ code: z.string(), label: z.string(), detail: z.string() })),
});
const reportFinding = z.object({
    id: z.string(),
    title: z.string(),
    detail: z.string(),
    calculation: z.string(),
    target: z.enum(["comparison", "products", "categories", "stores"]),
    targetId: z.string().nullable(),
});
const reportMetrics = z.object({
    scannedStores: count,
    locationCount: count,
    locatedCaptures: count,
    scannedDates: z.array(date),
    lastScanAt: z.string().datetime().nullable(),
    captures: count,
    analyzedCaptures: count,
    scannedDays: count,
    observedFacings: count,
    linkedFacings: count,
    facings: count,
    frontFacings: count,
    backFacings: count,
    sideFacings: count,
    unknownOrientationFacings: count,
    needsReviewFacings: count,
    productCount: count,
    imageCount: count,
    lastObservedAt: z.string().datetime().nullable(),
    observedShare: metric,
    frontFacingShare: metric,
});
export const brandReportResponse = z.object({
    definitionVersion: z.literal("brand-observation-report/v1"),
    brand: z.string(),
    from: date,
    to: date,
    previousFrom: date,
    previousTo: date,
    computedAt: z.string().datetime(),
    current: reportMetrics,
    quality: reportQuality,
    findings: z.array(reportFinding).max(5),
    comparison: z.object({
        matchedStoreCount: count,
        currentStoreCount: count,
        previousStoreCount: count,
        matchedLocationCount: count,
        current: reportMetrics,
        previous: reportMetrics,
    }),
    products: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        articleCode: z.string().nullable(),
        brand: z.string().nullable(),
        categoryId: z.string().uuid().nullable(),
        categoryName: z.string().nullable(),
        facings: count,
        frontFacings: count,
        backFacings: count,
        sideFacings: count,
        unknownOrientationFacings: count,
        imageCount: count,
        lastObservedAt: z.string().datetime(),
    })),
    categories: z.array(z.object({
        id: z.string().uuid().nullable(),
        name: z.string().nullable(),
        facings: count,
        observedFacings: count,
        linkedFacings: count,
        observedShare: metric,
    })),
    stores: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        current: reportMetrics,
        previous: reportMetrics,
    })),
    trend: z.array(catalogTrendResponse.shape.points.element),
    evidence: z.array(evidence.extend({ storeName: z.string() })),
});
export const categoryReportResponse = brandReportResponse
    .omit({ brand: true, categories: true })
    .extend({
    definitionVersion: z.literal("category-observation-report/v1"),
    category: z.object({ id: z.string().uuid(), name: z.string() }),
    brands: z.array(z.object({
        name: z.string().nullable(),
        facings: count,
        frontFacings: count,
        productCount: count,
        imageCount: count,
        observedShare: metric,
    })),
});
//# sourceMappingURL=brands.schemas.js.map